// ABOUTME: Auth0 callback handler page with user provisioning.
// ABOUTME: Handles redirect after Auth0 authentication, provisions user if needed.

import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle, ArrowLeft, Sparkles } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

type ProvisioningState = 'authenticating' | 'provisioning' | 'complete' | 'error';

export function CallbackPage() {
  const { isAuthenticated, isLoading: authLoading, error: authError } = useAuth();
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  const [provisioningState, setProvisioningState] = useState<ProvisioningState>('authenticating');
  const [provisioningError, setProvisioningError] = useState<string | null>(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);

  // Get stored return path from sessionStorage
  const getReturnPath = (): string => {
    const storedPath = sessionStorage.getItem('auth_return_to');
    if (storedPath) {
      sessionStorage.removeItem('auth_return_to');
      return storedPath;
    }
    return '/';
  };

  // Handle provisioning after authentication completes
  useEffect(() => {
    async function ensureProvisioned() {
      if (!isAuthenticated || authLoading) {
        return;
      }

      setProvisioningState('provisioning');

      try {
        // Get the access token
        const token = await getAccessTokenSilently();

        // Call the ensure-provisioned endpoint
        // VITE_API_URL already includes /v1 (e.g., /api/v1 or http://localhost:8000/v1)
        const apiUrl = import.meta.env.VITE_API_URL || '/v1';
        const response = await fetch(
          `${apiUrl}/auth/ensure-provisioned`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || `Provisioning failed: ${response.status}`);
        }

        const data = await response.json();

        // Redirect to verification page if email is not verified
        if (data.email_verified === false) {
          console.log('User email not verified:', data.user.email);
          setNeedsVerification(true);
          setProvisioningState('complete');
          return;
        }

        if (data.provisioned) {
          console.log('New user provisioned:', data.user.email);
          setNeedsOnboarding(true);
        } else if (!data.user.onboarding_completed) {
          console.log('Existing user, onboarding incomplete:', data.user.email);
          setNeedsOnboarding(true);
        } else {
          console.log('Existing user found:', data.user.email);
        }

        setProvisioningState('complete');
      } catch (err) {
        console.error('Provisioning error:', err);
        setProvisioningError(err instanceof Error ? err.message : 'Failed to set up your account');
        setProvisioningState('error');
      }
    }

    ensureProvisioned();
  }, [isAuthenticated, authLoading, getAccessTokenSilently]);

  // Handle case where user navigates directly to callback without auth flow
  useEffect(() => {
    if (!authLoading && !isAuthenticated && !authError) {
      // No auth in progress, redirect to login
      navigate('/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, authError, navigate]);

  // If provisioning complete, redirect appropriately
  if (provisioningState === 'complete') {
    // Unverified users go to the email verification gate
    if (needsVerification) {
      return <Navigate to="/verify-email" replace />;
    }
    // Users who haven't completed onboarding go to onboarding flow
    if (needsOnboarding) {
      return <Navigate to="/onboarding" replace />;
    }
    // Users who completed onboarding go to their intended destination or home
    return <Navigate to={getReturnPath()} replace />;
  }

  // Show auth error state
  if (authError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-900 p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-surface-0 dark:bg-surface-800 rounded-sm p-12 shadow-card border border-surface-200 dark:border-surface-700 max-w-md w-full"
        >
          <div className="flex justify-center mb-8">
            <div className="w-16 h-16 rounded-sm bg-error-light dark:bg-error/20 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-error" />
            </div>
          </div>

          <h1 className="text-2xl font-semibold text-surface-800 dark:text-surface-100 mb-2 text-center">
            Authentication Failed
          </h1>
          <p className="text-surface-600 dark:text-surface-400 text-center mb-6">
            {authError.message || 'An error occurred during authentication. Please try again.'}
          </p>

          <Button
            onClick={() => navigate('/login', { replace: true })}
            data-testid="back-to-login-button"
            className="w-full"
            size="lg"
            leftIcon={<ArrowLeft className="w-5 h-5" />}
          >
            Back to Login
          </Button>
        </motion.div>
      </div>
    );
  }

  // Show provisioning error state
  if (provisioningState === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-900 p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-surface-0 dark:bg-surface-800 rounded-sm p-12 shadow-card border border-surface-200 dark:border-surface-700 max-w-md w-full"
        >
          <div className="flex justify-center mb-8">
            <div className="w-16 h-16 rounded-sm bg-error-light dark:bg-error/20 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-error" />
            </div>
          </div>

          <h1 className="text-2xl font-semibold text-surface-800 dark:text-surface-100 mb-2 text-center">
            Setup Failed
          </h1>
          <p className="text-surface-600 dark:text-surface-400 text-center mb-6">
            {provisioningError || 'Failed to set up your account. Please try again.'}
          </p>

          <Button
            onClick={() => navigate('/login', { replace: true })}
            data-testid="back-to-login-button"
            className="w-full"
            size="lg"
            leftIcon={<ArrowLeft className="w-5 h-5" />}
          >
            Back to Login
          </Button>
        </motion.div>
      </div>
    );
  }

  // Show provisioning state (setting up workspace)
  if (provisioningState === 'provisioning') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-900 p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-surface-0 dark:bg-surface-800 rounded-sm p-12 shadow-card border border-surface-200 dark:border-surface-700 max-w-md w-full text-center"
        >
          <div className="flex justify-center mb-8">
            <div className="w-16 h-16 rounded-sm bg-primary-light dark:bg-primary/20 flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-primary animate-pulse" />
            </div>
          </div>

          <h1 className="text-2xl font-semibold text-surface-800 dark:text-surface-100 mb-2">
            Setting up your workspace...
          </h1>
          <p className="text-surface-600 dark:text-surface-400 mb-6">
            We're preparing everything for you. This will only take a moment.
          </p>

          <div className="flex justify-center">
            <LoadingSpinner size="md" />
          </div>
        </motion.div>
      </div>
    );
  }

  // Show loading state while authentication completes
  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-900 p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-surface-0 dark:bg-surface-800 rounded-sm p-12 shadow-card border border-surface-200 dark:border-surface-700 max-w-md w-full text-center"
      >
        <div className="flex justify-center mb-8">
          <LoadingSpinner size="lg" />
        </div>

        <h1 className="text-2xl font-semibold text-surface-800 dark:text-surface-100 mb-2">
          Completing authentication...
        </h1>
        <p className="text-surface-600 dark:text-surface-400">
          Please wait while we verify your credentials.
        </p>
      </motion.div>
    </div>
  );
}

export default CallbackPage;
